// Shell de la app autenticada — sistema de diseño "Audiophile Precision".
//
// Reestructuración (mobile-first):
//  - Se elimina la barra de título superior redundante (con el botón de sesión).
//    Cada pantalla arranca con su propio título semántico (ver `PageHeader`).
//  - Tab-bar inferior de 5 secciones, de izquierda a derecha:
//      Búsqueda · Ingresar · Catálogo (central, disco de vinilo) · Deseados · Menú.
//  - La gestión de sesión (perfil / cerrar sesión) vive ahora dentro de "Menú"
//    junto al resto de módulos secundarios (racks, analítica, auditoría, admin…).
//  - La navegación respeta permisos (Req. 19.10): las entradas sin permiso se
//    ocultan. "Menú" siempre es visible (contiene la sesión del usuario).

import { useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './auth/AuthContext.js';
import { ErrorBoundary } from './ui/ErrorBoundary.js';
import { OnboardingTour } from './onboarding/OnboardingTour.js';
import type { Feature } from './types.js';

interface NavEntry {
  to: string;
  /** Clave de traducción del rótulo (i18n). */
  labelKey: string;
  icon: string;
  /** Funcionalidad requerida para ver la entrada (o `null` = siempre). */
  feature: Feature | null;
  /** Pestaña central destacada (disco de vinilo). */
  central?: boolean;
  /** Ancla del onboarding guiado (atributo `data-tour`). */
  tour?: string;
}

/**
 * Pestañas de la tab-bar inferior, en orden de izquierda a derecha.
 * Catálogo es la pestaña central destacada (colección principal).
 * "Menú" siempre visible: agrupa módulos secundarios y la sesión del usuario.
 */
const TABS: NavEntry[] = [
  {
    to: '/quick-lookup',
    labelKey: 'nav.search',
    icon: 'search',
    feature: 'quick_lookup',
    tour: 'nav-search',
  },
  { to: '/add', labelKey: 'nav.add', icon: 'add_circle', feature: 'ingestion', tour: 'nav-add' },
  {
    to: '/',
    labelKey: 'nav.catalog',
    icon: 'album',
    feature: 'catalog',
    central: true,
    tour: 'nav-catalog',
  },
  {
    to: '/wishlist',
    labelKey: 'nav.wishlist',
    icon: 'bookmark',
    feature: 'wishlist',
    tour: 'nav-wishlist',
  },
  { to: '/menu', labelKey: 'nav.menu', icon: 'grid_view', feature: null, tour: 'nav-menu' },
];

export function Layout() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const location = useLocation();

  const tabs = useMemo(
    () => TABS.filter((t) => (t.feature === null ? true : hasPermission(t.feature, 'read'))),
    [hasPermission],
  );

  return (
    <div className="min-h-screen flex flex-col bg-surface-base text-on-surface font-body-md text-body-md">
      {/* Skip-link accesible: permite a usuarios de teclado saltar la tab-bar e
          ir directo al contenido. Visible solo al recibir foco. */}
      <a href="#main-content" className="skip-link">
        {t('common.skipToContent')}
      </a>
      {/* Contenido — cada vista abre con su propio título (PageHeader), que
          incluye el botón "Salir" a la derecha.
          Mobile-first: las vistas (campos, botones, tarjetas) están diseñadas
          para una columna única acotada. En escritorio NO estiramos ese ancho
          (los formularios quedarían deformes), sino que mantenemos el canvas de
          lectura (max-w-2xl ≈ 672px) CENTRADO sobre el fondo, para preservar el
          diseño de la app en pantallas grandes. */}
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 w-full max-w-2xl mx-auto px-margin-mobile pt-safe pb-24 flex flex-col"
      >
        {/* Barrera de error por ruta: si una vista revienta al renderizar, se
            muestra un aviso recuperable en vez de dejar la PWA en negro. Se
            resetea al cambiar de ruta (resetKey = pathname). */}
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Tab-bar inferior de 5 secciones. Mismo ancho que el canvas de la app
          (max-w-2xl) y centrada; las pestañas se reparten a lo ancho para que
          los iconos no queden amontonados. */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 pb-safe bg-surface-deck/90 backdrop-blur-xl shadow-[0_-1px_16px_rgba(0,0,0,0.55)] max-w-2xl mx-auto"
        aria-label={t('common.mainNav')}
      >
        <div className="flex justify-around items-center h-16 px-space-xs">
          {tabs.map((tab) => {
            if (tab.central) {
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end
                  data-tour={tab.tour}
                  className="flex flex-col items-center justify-center flex-1 h-full min-w-[56px] group"
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`w-12 h-12 -mt-4 rounded-full flex items-center justify-center transition-transform active:scale-95 ${
                          isActive
                            ? 'bg-primary-container text-on-primary-fixed shadow-[0_0_16px_rgba(56,189,248,0.45)]'
                            : 'bg-surface-rack text-on-surface-variant shadow-[0_0_10px_rgba(0,0,0,0.4)]'
                        }`}
                      >
                        <span className="material-symbols-outlined text-[26px]">{tab.icon}</span>
                      </span>
                      <span
                        className={`font-label-caps text-label-caps mt-0.5 tracking-wider uppercase ${
                          isActive ? 'text-primary' : 'text-on-surface-variant'
                        }`}
                      >
                        {t(tab.labelKey)}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            }
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                data-tour={tab.tour}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full min-w-[56px] transition-colors group ${
                    isActive ? 'text-primary' : 'text-on-surface-variant'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className="relative flex flex-col items-center">
                      <span className="material-symbols-outlined text-[22px] transition-transform group-active:scale-90">
                        {tab.icon}
                      </span>
                      <div
                        className={`h-0.5 rounded-full transition-all duration-200 mt-0.5 ${
                          isActive ? 'w-6 bg-primary-container' : 'w-0 bg-transparent'
                        }`}
                      />
                    </div>
                    <span className="font-label-caps text-label-caps mt-0.5 tracking-wider truncate uppercase">
                      {t(tab.labelKey)}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Onboarding guiado: overlay/spotlight que se superpone a la app. Solo
          pinta algo cuando hay un recorrido activo (primer acceso o reinicio). */}
      <OnboardingTour />
    </div>
  );
}
